import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  BuildingLibraryIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/16/solid';
import { Dialog } from '../../common/Catalyst/dialog';
import { Button } from '../../common/Catalyst/button';
import { BankConnectionReturn } from '../../clients/rust-api-client';
import { bankService } from '../../clients/ApiClients';
import { ErrorAlert } from '../../common/Custom/ErrorAlert';
import { PropertyPicker } from '../../violation/linear/PropertyPicker';
import {
  BANK_OPTIONS,
  BankConnectionDraft,
  bankConnectionError,
  buildBankConnectionCreate,
  credentialKey,
  emptyBankConnectionDraft,
  isCredentialRejection,
  missingForBankConnection,
} from './bankConnectionMeta';

/**
 * Connect a company to a bank the Linear way (#665): a large name for the
 * connection, the bank as a chip, and the credential as labelled fields.
 * It replaces BankConnectionList's label-over-input dialog.
 *
 * **Creating a connection verifies the credential against the live bank,**
 * and repeated invalid credentials lock the account until the bank resets
 * it. So this modal never retries on its own. After the bank refuses a
 * credential, Create stays disabled until the client ID or secret changes,
 * because the same pair sent again can only fail again and add to the
 * count.
 *
 * The Bank chip is the same picker the Banking page's sections use to
 * choose a connection (`ConnectionPicker`); with one bank on offer it is
 * shown, not pickable.
 *
 * Opened only from the Banking page, never from ⌘K or the sidebar: it is
 * rare, Owner-only, and enters a credential that controls money.
 */
interface Props {
  open: boolean;
  companyId: string | null;
  onClose: () => void;
  onCreated: (connection: BankConnectionReturn) => void;
}

export const CreateBankConnectionModal: React.FC<Props> = ({
  open,
  companyId,
  onClose,
  onCreated,
}) => {
  const [draft, setDraft] = useState<BankConnectionDraft>(() =>
    emptyBankConnectionDraft(companyId)
  );
  const [rejectedKey, setRejectedKey] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<{ title: string; message: string } | null>(
    null
  );

  useEffect(() => {
    if (!open) return;
    setDraft(emptyBankConnectionDraft(companyId));
    setRejectedKey(null);
    setError(null);
  }, [open, companyId]);

  const set =
    <K extends keyof BankConnectionDraft>(key: K) =>
    (value: BankConnectionDraft[K]) =>
      setDraft((d) => ({ ...d, [key]: value }));

  const missing = missingForBankConnection(draft);
  const unchangedSinceRejection =
    rejectedKey !== null && rejectedKey === credentialKey(draft);
  const request = buildBankConnectionCreate(draft, rejectedKey);
  const canSubmit = !!request && !saving;
  const bank = BANK_OPTIONS.find((b) => b.providerKey === draft.providerKey);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!request || saving) return;
    setSaving(true);
    setError(null);
    try {
      const response = await bankService.createBankConnection({
        bankConnectionCreate: request,
      });
      onCreated(response.data);
      onClose();
    } catch (err) {
      // No retry, ever: surface it and let the person fix the credential.
      setError(bankConnectionError(err));
      if (isCredentialRejection(err)) setRejectedKey(credentialKey(draft));
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'w-full rounded-md border border-line bg-transparent px-2 py-1 text-[13px] text-fg placeholder:text-fg-faint focus:outline-none focus:ring-2 focus:ring-accent';
  const labelClass = 'flex flex-col gap-1 text-[12px] text-fg-muted';

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} size="2xl">
      <form onSubmit={submit} aria-label="New bank connection" autoComplete="off">
        <p className="text-[12px] font-medium text-fg-muted">New bank connection</p>

        <input
          autoFocus
          value={draft.label}
          onChange={(e) => set('label')(e.target.value)}
          placeholder="Connection name, e.g. Operating — Columbia"
          aria-label="Label"
          className="mt-2 w-full border-0 bg-transparent p-0 text-xl font-semibold text-fg placeholder:text-fg-faint focus:outline-none focus:ring-0"
        />

        <div className="mt-3 flex flex-wrap items-center gap-1 border-t border-line/60 pt-3">
          <PropertyPicker
            options={BANK_OPTIONS.map((b) => ({ value: b.providerKey, label: b.label }))}
            value={draft.providerKey}
            onChange={set('providerKey')}
            placeholder="Bank…"
            ariaLabel={`Bank: ${bank?.label ?? draft.providerKey}`}
            readOnly={BANK_OPTIONS.length < 2}
            trigger={
              <>
                <BuildingLibraryIcon className="h-3.5 w-3.5 text-fg-muted" aria-hidden />
                {bank?.label ?? draft.providerKey}
              </>
            }
          />
        </div>

        <fieldset className="mt-4">
          <legend className="text-[12px] font-medium text-fg-secondary">Credential</legend>
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <label className={labelClass}>
              Client ID
              <input
                value={draft.clientId}
                onChange={(e) => set('clientId')(e.target.value)}
                spellCheck={false}
                className={inputClass}
              />
            </label>
            <label className={labelClass}>
              Client secret
              <input
                type="password"
                autoComplete="new-password"
                value={draft.clientSecret}
                onChange={(e) => set('clientSecret')(e.target.value)}
                className={inputClass}
              />
              <span>Stored encrypted; it is never shown again.</span>
            </label>
            <label className={clsx(labelClass, 'sm:col-span-2')}>
              Lockbox company ID (optional)
              <input
                value={draft.lockboxCompanyId}
                onChange={(e) => set('lockboxCompanyId')(e.target.value)}
                placeholder="e.g. 9998"
                spellCheck={false}
                className={clsx(inputClass, 'sm:w-48')}
              />
              <span>
                The bank-assigned Lockbox Management CO ID, used as Column A of
                the payer file. Add it once the bank provides it; payer files
                cannot be submitted until it is set.
              </span>
            </label>
          </div>
        </fieldset>

        <ErrorAlert className="mt-3" title={error?.title}>
          {error?.message}
        </ErrorAlert>

        <p className="mt-4 flex items-start gap-1.5 text-[12px] text-fg-muted">
          <ExclamationTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          {unchangedSinceRejection
            ? 'The bank refused this client ID and secret. Change one before trying again; repeating a refused credential can lock the account.'
            : 'Create checks the credential against the bank before storing it. Avoid repeated attempts with a wrong credential: the bank may lock the account.'}
        </p>

        <div className="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-line/60 pt-4">
          {missing.length > 0 && (draft.clientId || draft.clientSecret || draft.label) && (
            <p className="mr-auto text-[12px] text-fg-muted">
              Still needs {missing.join(', ')}.
            </p>
          )}
          <Button plain type="button" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            color="sky"
            type="submit"
            disabled={!canSubmit}
            title={missing.length ? `Add ${missing.join(', ')}` : undefined}
          >
            {saving ? 'Verifying with the bank…' : 'Create connection'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
};
